import { useEffect, useMemo, useState } from "react";
import { Loader2Icon } from "lucide-react";
import { toast } from "sonner";
import {
  useRiskProfileQuestions,
  useSubmitRiskProfile,
} from "creso-core/hooks";
import { ProgressPills } from "./progress-pills";
import { QuestionStep } from "./question-step";
import { ResultStep } from "./result-step";
import { TIER_DESCRIPTIONS, TIER_SCORES } from "./tier-meta";
import {
  RiskProfileAnswers,
  RiskProfilePhase,
  RiskProfileQuestion,
  RiskProfileResult,
  RiskProfilerProps,
} from "./types";

const buildResult = (tier: RiskProfileResult["tier"]): RiskProfileResult => ({
  tier,
  score: TIER_SCORES[tier],
  description: TIER_DESCRIPTIONS[tier],
});

export function RiskProfiler({
  clientId,
  onComplete,
  onClose,
  initialTier,
  HeaderComponent,
  IntroComponent,
}: RiskProfilerProps) {
  const [phase, setPhase] = useState<RiskProfilePhase>(
    initialTier ? "result" : "loading"
  );
  const [questionIndex, setQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<RiskProfileAnswers>({});
  const [result, setResult] = useState<RiskProfileResult | null>(
    initialTier ? buildResult(initialTier) : null
  );

  const { data: rawQuestions, isError: questionsErrored } =
    useRiskProfileQuestions(phase !== "result");
  const { mutateAsync: submitAnswers, isPending: submitting } =
    useSubmitRiskProfile();

  const questions: RiskProfileQuestion[] = useMemo(
    () =>
      (rawQuestions ?? []).map((question) => ({
        id: String(question.id),
        text: question.question_text,
        options: question.options.map((option) => ({
          id: String(option.id),
          label: option.text,
        })),
      })),
    [rawQuestions]
  );

  useEffect(() => {
    if (questionsErrored) {
      toast.error("Failed to load risk assessment questions.");
      return;
    }
    if (phase === "loading" && questions.length > 0) {
      setPhase(IntroComponent ? "intro" : "questions");
    }
  }, [questions, questionsErrored, phase, IntroComponent]);

  const currentQuestion = questions[questionIndex];
  const isLastQuestion = questionIndex === questions.length - 1;

  const handleNext = async () => {
    if (!isLastQuestion) {
      setQuestionIndex((i) => i + 1);
      return;
    }

    try {
      const responses = Object.entries(answers).map(
        ([questionId, optionId]) => ({
          questionId: Number(questionId),
          optionId,
        })
      );
      const response = await submitAnswers({
        clientId,
        responses,
      });
      const computed = buildResult(response.risk_profile);
      setResult(computed);
      onComplete?.(computed);
      setPhase("result");
    } catch {
      toast.error("Failed to compute risk profile. Please try again.");
    }
  };

  const handlePrevious = () => {
    setQuestionIndex((i) => Math.max(0, i - 1));
  };

  const handleRetake = () => {
    setAnswers({});
    setQuestionIndex(0);
    setResult(null);
    setPhase("loading");
  };

  return (
    <div className="flex flex-col gap-3 h-full min-h-0">
      {HeaderComponent && (
        <HeaderComponent
          phase={phase}
          questionIndex={questionIndex}
          totalQuestions={questions.length}
        />
      )}
      <div className="flex-1 min-h-0 flex flex-col">
        {phase === "loading" && (
          <div className="flex flex-1 items-center justify-center">
            <Loader2Icon className="size-5 animate-spin text-muted-foreground" />
          </div>
        )}

        {phase === "intro" && IntroComponent && (
          <IntroComponent onProceed={() => setPhase("questions")} />
        )}

        {phase === "questions" && currentQuestion && (
          <>
            <div className="pb-6 border-b border-border flex flex-col gap-2">
              <span className="text-xs text-muted-foreground">
                Question {questionIndex + 1} of {questions.length}
              </span>
              <ProgressPills
                step={questionIndex + 1}
                total={questions.length}
              />
            </div>
            <QuestionStep
              question={currentQuestion}
              value={answers[currentQuestion.id]}
              onChange={(optionId) =>
                setAnswers((a) => ({ ...a, [currentQuestion.id]: optionId }))
              }
              onNext={handleNext}
              onPrevious={handlePrevious}
              canGoPrevious={questionIndex > 0}
              isLastQuestion={isLastQuestion}
              submitting={submitting}
            />
          </>
        )}

        {phase === "result" && result && (
          <ResultStep
            result={result}
            onDone={onClose ?? (() => {})}
            onRetake={
              Object.keys(answers).length === 0 ? handleRetake : undefined
            }
          />
        )}
      </div>
    </div>
  );
}
